/**
 * Mobile Column Configuration Utilities
 *
 * Resolves mobile display config for columns. Columns with explicit `mobile`
 * property use that; others get sensible defaults inferred from existing
 * column properties (persisted, default, field name patterns).
 *
 * This is generic — it works across all menus (SaleInvoices, Purchases,
 * POS, CustomInvoices, BankJournal, Payroll, etc.) without hardcoded fields.
 */

const ACTION_PATTERNS = /^(actions|print|sendEmail|delete|edit|download|thermal)$/i;
const FINANCIAL_PATTERNS = /amount|total|price|fee|tax|balance|credit|debit|exchange/i;
const STATUS_PATTERNS = /status|state|confirmed|verified|approved/i;
const DATE_PATTERNS = /date|datetime|time|period/i;

const inferGroup = (field) => {
  if (!field) return "other";
  if (ACTION_PATTERNS.test(field)) return "actions";
  if (STATUS_PATTERNS.test(field)) return "status";
  if (FINANCIAL_PATTERNS.test(field)) return "financial";
  if (DATE_PATTERNS.test(field)) return "dates";
  return "other";
};

const inferPriority = (column) => {
  if (ACTION_PATTERNS.test(column.field)) return "action";
  if (column.persisted === true) return 1;
  if (column.default === true) return 2;
  return 3;
};

/**
 * Resolves the mobile configuration for a single column.
 * If the column has an explicit `mobile` property, it is used (with defaults filled in).
 * Otherwise, config is inferred from existing column properties.
 */
export const resolveMobileConfig = (column) => {
  const explicit = column.mobile || {};
  const inferred = {
    priority: inferPriority(column),
    isTitle: false,
    isBadge: false,
    group: inferGroup(column.field),
  };

  return {
    ...inferred,
    ...explicit,
    field: column.field,
    header: column.header,
    body: column.body,
    mobileBody: explicit.mobileBody,
    filterType: column.filterType,
    filterConfig: column.filterConfig,
    filter: column.filter,
    sortable: column.sortable,
    sortField: column.sortField,
    dataType: column.dataType,
    persisted: column.persisted,
  };
};

/**
 * Categorizes an array of columns into groups for card rendering.
 * Respects selectedColumns visibility — hidden columns are excluded.
 */
export const categorizeColumns = (columns) => {
  const resolved = columns.map(resolveMobileConfig);

  const title = resolved.find((c) => c.isTitle);
  const badges = resolved.filter((c) => c.isBadge);
  const actions = resolved.filter((c) => c.priority === "action");
  const primary = resolved.filter(
    (c) => c.priority === 1 && !c.isTitle && !c.isBadge && c.priority !== "action"
  );
  const secondary = resolved.filter((c) => c.priority === 2);
  const hidden = resolved.filter((c) => c.priority === 3);
  const financial = resolved.filter(
    (c) => c.group === "financial" && c.priority === 1
  );
  const sortable = resolved.filter((c) => c.sortable);
  const filterable = resolved.filter((c) => c.filter);

  return {
    title,
    badges,
    actions,
    primary,
    secondary,
    hidden,
    financial,
    sortable,
    filterable,
    all: resolved,
  };
};
